interface NominatimAddress {
  suburb?: string
  neighbourhood?: string
  village?: string
  town?: string
  city_district?: string
  city?: string
  county?: string
  state?: string
  country?: string
}

interface NominatimResponse {
  address: NominatimAddress
}

/**
 * Get the user's current location via the browser Geolocation API,
 * reverse-geocoded to a "Locality, Region, Country" string using
 * OpenStreetMap Nominatim.
 *
 * - First call triggers the browser permission prompt (one-time per domain).
 * - Subsequent calls resolve silently with no prompt.
 * - Returns null if permission denied, unavailable, or reverse-geocode fails.
 */
export async function getBrowserLocation(): Promise<string | null> {
  if (!navigator.geolocation) return null

  try {
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // accept cached position up to 5 min old
        })
      }
    )

    const { latitude, longitude } = position.coords
    const url =
      `https://nominatim.openstreetmap.org/reverse?` +
      `lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`

    const response = await fetch(url, {
      headers: { 'User-Agent': 'TreatWise/1.0' },
    })
    if (!response.ok) return null

    const data: NominatimResponse = await response.json()
    const addr = data.address
    if (!addr) return null

    // Most specific locality: suburb > neighbourhood > village > town
    const locality =
      addr.suburb || addr.neighbourhood || addr.village || addr.town || ''

    const district = addr.city_district || ''
    const city = addr.city || ''
    const state = addr.state || ''
    const country = addr.country || ''

    // Build full location, omitting empty or duplicate parts
    // e.g. "Woodford Green, London Borough of Redbridge, Greater London, England, United Kingdom"
    // or "Falomo, Eti Osa, Lagos, Nigeria"
    const parts: string[] = []
    const seen = new Set<string>()
    for (const part of [locality, district, city, state, country]) {
      if (part && !seen.has(part)) {
        parts.push(part)
        seen.add(part)
      }
    }

    if (parts.length >= 2) {
      return parts.join(', ')
    }

    return null
  } catch {
    return null
  }
}
